import {
    FC, memo, useEffect, useMemo, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Constants, {
    COUNTRY_CODE,
    DOCUMENT_TYPES,
    FALLBACK_GENDER
} from '../../../Utils/Constants';
import DI from '../../../Utils/DI';
import Utils from '../../../Utils/Utils';
import Button from '../Button/Button';
import Card from '../Card/Card';
import Loader from '../Loader/Loader';
import GeneralInformationScreen from './Style';
import Dialog from '../Dialog/Dialog';
import FieldRenderer from '../FieldRenderer/FieldRenderer';
import Modal from '../Modal/Modal';
import { Gender, UserProps } from '../../../Business/data/User';
import IDocumentManager from '../../../Business/IDocumentManager';
import DocumentManager from '../../../Business/DocumentManager';
import IUserManager from '../../../Business/UserManager/IUserManager';
import IChallengeManager from '../../../Business/IChallengeManager';
import {
    ERRORS_DESCRIPTION,
    ERROR_CODES,
    IError,
    REQUEST_REVERSE_OTP_RECOVERABLE_ERRORS,
    BackendError
} from '../../../Utils/Constants/Errors';
import VodafoneWhiteLoader from '../../Assets/loaders/vodafone_white_loader.json';
import IAnalyticsManager from '../../../Business/IAnalyticsManager';
import useGaEventTracker from '../../Hooks/useGaEventTracker';
import ISessionManager from '../../../Business/ISessionManager';
import IGeneralInformationProps, { IUserDataItem, IUserPersonalInformation } from './general-information-types';
import Checkbox from '../Checkbox/Checkbox';
import {
    ISteps,
    redirectToPersonalDocuments
} from '../../../Utils/Constants/FormSteps';
import useStepContext from '../../Hooks/useStepContext';
import { IResultScreenProps } from '../ResultScreen/result-screen-types';
import IRegistrationManager from '../../../Business/RegistrationManager/IRegistrationManager';
import IDataManager from '../../../Business/IDataManager';
import customerRegistrationManager from '../../../Business/RegistrationManager/CustomerRegistrationManager';
import AgentRegistrationManager from '../../../Business/RegistrationManager/AgentRegistrationManager';
import CloudUploadIcon from '../../Assets/icons/UploadIcon';
import { MAX_ADDITIONAL_DOCS } from '../../../Utils/KYCConfigs';

const GeneralInformation: FC<IGeneralInformationProps> = ({
    userPersonalInformation,
    setUserPersonalInformation,
    userAddressInformation,
    setUserAddressInformation,
    userAdditionalInformation,
    setUserAdditionalInformation,
    nextStepHandler,
    identityDocuments
}) => {
    const dataManager: IDataManager = DI.get('DataManager');
    const userManager: IUserManager = dataManager.convertToUserManager();
    const documentManager: IDocumentManager = DI.get('DocumentManager');
    const challengeManager: IChallengeManager = DI.get('ChallengeManager');
    const analyticsManager: IAnalyticsManager = DI.get('AnalyticsManager');
    const sessionManager: ISessionManager = DI.get('SessionManager');
    const registrationManager: IRegistrationManager = dataManager.convertToRegistrationManager();
    const isAgentLogged: boolean | undefined = useMemo(() => sessionManager.getSession()?.getIsValid(), []);
    const { eventTracker } = useGaEventTracker();
    const {
        currentFormStep, whitelistedUser, documentType, setDocumentType, othersType, setOthersType, captureBackSide, setCaptureBackSide
    } = useStepContext();
    if (!currentFormStep?.KEY) return null;

    useEffect(() => {
        customerRegistrationManager.setDocumentTypeCallback(setDocumentType);
    }, [setDocumentType]);
    const {
        selectedDocumentId,
        selectedDocumentEn,
        selectedDocumentFr
    } = currentFormStep?.PROPS || {};
    const isMetaDataForm = currentFormStep.KEY.includes(Constants.UTILS.METADATA);
    const whitelistedResponse = customerRegistrationManager.getWhiteListedResponse();
    const agentWhitelistedRepsonse = AgentRegistrationManager.getAgentWhiteListedResponse();
    const documentImages = documentManager.getProcessedDocuments();

    const [additionalDocs, setAdditionalDocs] = useState<(string | null)[]>([]);

    useEffect(() => {
        const selectedOthersLabel = userPersonalInformation.othersType?.selectedItem?.description
            || userPersonalInformation.othersType?.selectedItem?.label;
        const match = (identityDocuments || []).find((d: any) => d?.en === selectedOthersLabel || d?.fr === selectedOthersLabel);
        if (match && match.documentType) {
            setDocumentType(match.documentType);
            setCaptureBackSide(!!match.captureBackSide);
        }
    }, [userPersonalInformation.othersType]);

    const [currentDocIndex, setCurrentDocIndex] = useState<number | null>(null);

    const registrationData = userManager.getUserInformation();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { FORM_STEPS } = Constants;
    const [showDialog, setShowDialog] = useState({
        show: false,
        title: t('generalInformation.edit'),
        description: t('generalInformation.confirmEdit')
    });

    const [error, setError] = useState<IError>({
        hasError: false,
        title: t('error.apiErrors.somethingWentWrong'),
        description: t('general.pleaseRefresh')
    });

    const closeDialog = (): void => setShowDialog((prev) => {
        const isAdditional = imageToEdit === 'ADDITIONAL';
        const title = isAdditional
            ? (!whitelistedUser
                ? t('generalInformation.add')
                : (!isMetaDataForm
                    ? t('generalInformation.edit')
                    : t('generalInformation.add')))
            : t('generalInformation.edit');

        const description = isAdditional
            ? (!whitelistedUser
                ? t('generalInformation.confirmAdd')
                : (!isMetaDataForm
                    ? t('generalInformation.confirmEdit')
                    : t('generalInformation.confirmAdd')))
            : t('generalInformation.confirmEdit');
        return {
            ...prev,
            show: false,
            title,
            description
        };
    });

    const showDialogHandler = (imageToEdit: string, index?: number) => {
        setShowDialog((prev) => ({ ...prev, show: true }));
        switch (imageToEdit) {
        case 'DOCUMENT':
            setShowDialog((prev) => ({
                ...prev,
                title: `${prev.title} ${t('generalInformation.document')} `,
                description: `${prev.description} ${isAgentLogged
                    ? t('generalInformation.customerDocument')
                    : t('generalInformation.personalDocument')}?`
            }));
            if (index !== undefined) {
                const processedAdditionalDocs = documentManager.getProcessedDocuments()
                    .filter((doc) => doc.documentType === DOCUMENT_TYPES.ADDITIONAL_DOCUMENTS.toLowerCase());

                const doc = processedAdditionalDocs[index];
                setCurrentDocIndex(doc?.index ?? null);
                setImageToEdit(imageToEdit);
            }
            break;

        case 'SELFIE':
            setShowDialog((prev) => ({
                ...prev,
                title: `${prev.title} ${isAgentLogged ? t('generalInformation.photo') : t('generalInformation.selfie')}`,
                description: `${prev.description} ${isAgentLogged
                    ? t('generalInformation.customerSelfie')
                    : t('generalInformation.userSelfie')}?`
            }));
            setImageToEdit(imageToEdit);
            break;
        case 'ADDITIONAL':
            const isAdditionalAdd = !whitelistedUser || (whitelistedUser && isMetaDataForm);
            setShowDialog((prev) => ({
                ...prev,
                title: 'Additional file',
                description: isAdditionalAdd
                    ? `${t('generalInformation.confirmAdd')} ${isAgentLogged
                        ? t('generalInformation.customerDocument')
                        : t('generalInformation.personalDocument')}?`
                    : `${t('generalInformation.confirmEdit')} ${isAgentLogged
                        ? t('generalInformation.customerDocument')
                        : t('generalInformation.personalDocument')}?`
            }));
            setImageToEdit(imageToEdit);

            if (index !== undefined) {
                documentManager.setCurrentAdditionalIndex(index);
                setCurrentDocIndex(index);
            } else {
                documentManager.setCurrentAdditionalIndex(null);
                setCurrentDocIndex(null);
            }
            break;

        default:
            setShowDialog((prev) => ({
                ...prev,
                title: `${prev.title} ${t('generalInformation.data')} `,
                description: `${prev.description} ${t('generalInformation.dataDescription')}?`
            }));
        }
    };

    const [documentImage, setDocumentImage] = useState<any>({
        FRONT: null,
        BACK: null
    });

    useEffect(() => {
        const userInfo: UserProps = {
            firstName: userPersonalInformation.firstName.input,
            lastName: userPersonalInformation.lastName.input,
            placeOfBirth: userPersonalInformation.placeOfBirth.input,
            dateOfBirth: userPersonalInformation.dateOfBirth.input,
            gender: userPersonalInformation.gender.selectedItem?.key?.substring(7, 8)?.toUpperCase() as Gender,
            nationality: userPersonalInformation.citizen.selectedItem?.description || '',
            documentNumber: (userPersonalInformation[retrieveDocumentNumber()] as IUserDataItem).input,

            street: userAddressInformation.street.input,
            province: userAddressInformation.province.selectedItem?.description || '',
            town: userAddressInformation.town.selectedItem?.description || '',
            commune: userAddressInformation.commune.input,

            occupation: userAdditionalInformation.occupation.selectedItem?.value || '',
            income: userAdditionalInformation.income.selectedItem?.description,
            idCardIssuingBy: userAdditionalInformation.idCardIssuingBy.input,
            idCardIssuingDate: userAdditionalInformation.idCardIssuingDate.input,
            idCardIssuingPlace: userAdditionalInformation.idCardIssuingPlace?.selectedItem?.description,
            othersType: userPersonalInformation.othersType?.selectedItem?.description
        };

        userManager.saveUserInformation(userInfo);
    }, [userPersonalInformation, userAddressInformation, userAdditionalInformation, userManager]);

    useEffect(() => {
        // INFO: retrieve the captured images (excluding ADDITIONAL_DOCUMENTS) and store in an object
        (documentManager as DocumentManager).getProcessedDocuments()
            .filter((entry) => entry.documentType !== DOCUMENT_TYPES.ADDITIONAL_DOCUMENTS.toLowerCase())
            .forEach((entry) => {
                setDocumentImage((prev: any) => ({
                    ...prev,
                    [entry.orientation]: entry.file
                }));
            });
    }, []);

    useEffect(() => {
        const processed = documentManager.getProcessedDocuments();
        if (processed.length > 0) {
            const additional = processed.filter(
                (doc) => doc.documentType === DOCUMENT_TYPES.ADDITIONAL_DOCUMENTS.toLowerCase()
            );

            setAdditionalDocs(() => {
                const updated: string[] = [];
                additional.forEach((doc) => {
                    const idx = doc.index ?? 0;
                    while (updated.length <= idx) updated.push('');
                    updated[idx] = doc.file;
                });
                return updated.filter((doc) => doc !== '');
            });
        }
    }, []);

    // INFO: validate if all fields of the userPersonalInformation are valid (i.e isValid)
    const validPersonalInformation = Object.entries(userPersonalInformation).every(
        ([, value]) => value.isValid === true
    );

    // INFO: validate if all fields of the userAddressInformation are valid (i.e isValid)
    const validAddressInformation = Object.entries(userAddressInformation).every(
        ([, value]) => value.isValid === true
    );

    const validAdditionalInformation = Object.entries(userAdditionalInformation).every(
        ([, value]) => value.isValid === true
    );

    const [agentConfirmation, setAgentConfirmation] = useState(false);
    const [imageToEdit, setImageToEdit] = useState<string>('DOCUMENT');

    let SELECTED_DOCUMENT: string | undefined;
    if (whitelistedUser) {
        SELECTED_DOCUMENT = documentType?.toUpperCase();
    } else {
        SELECTED_DOCUMENT = documentManager.getProcessedDocuments()[0].documentType.toUpperCase();
    }
    const editImageHandler = () => {
        if (imageToEdit === 'DOCUMENT') {
            eventTracker({
                name: 'IMAGE_RETAKES',
                objectValue: { MODIFIED_IMAGE: SELECTED_DOCUMENT }
            });
            analyticsManager.resetDocumentUploadErrorCounter();
            if (!isMetaDataForm) {
                const dynamicProps = {
                    documentType,
                    captureBackSide,
                    selectedDocumentId,
                    selectedDocumentEn,
                    selectedDocumentFr,
                    identityDocuments
                };
                nextStepHandler({
                    ...FORM_STEPS.OTHERS_FRONT,
                    PROPS: dynamicProps
                });
            } else {
                nextStepHandler(redirectToPersonalDocuments(isAgentLogged));
            }
        }

        if (imageToEdit === 'SELFIE') {
            analyticsManager.resetLivenessCheckErrorCounter();
            analyticsManager.setAttemptsCounter(0);
            eventTracker({
                name: 'IMAGE_RETAKES',
                objectValue: { MODIFIED_IMAGE: 'Selfie' }
            });
            if (isAgentLogged) {
                nextStepHandler(FORM_STEPS.CUSTOMER_VALIDATION);
                return;
            }
            nextStepHandler(currentFormStep.PREVIOUS_STEP_HANDLER({ previousStep: currentFormStep, isAgentLogged }));
        }

        if (imageToEdit === 'ADDITIONAL') {
            analyticsManager.resetLivenessCheckErrorCounter();
            analyticsManager.setAttemptsCounter(0);
            eventTracker({
                name: 'IMAGE_RETAKES',
                objectValue: { MODIFIED_IMAGE: 'Additional File' }
            });

            const processedAdditionalDocs = documentManager.getProcessedDocuments()
                .filter((doc) => doc.documentType === DOCUMENT_TYPES.ADDITIONAL_DOCUMENTS.toLowerCase());

            let docIndexToEdit: number | undefined;
            if (currentDocIndex !== null) {
                const doc = processedAdditionalDocs[currentDocIndex];
                docIndexToEdit = doc?.index;
            }
            nextStepHandler({
                ...Constants.FORM_STEPS.ADDITIONAL_DOCUMENT,
                PROPS: { currentDocIndex: docIndexToEdit, lastStep: isMetaDataForm, fromStepKey: currentFormStep.KEY }
            });
        }
    };

    // INFO: Retrieves the correct property name for each document number.
    const retrieveDocumentNumber = (): string => ['passportNr', 'voterNr', 'drivingLicenseNr', 'othersNr']
        .find((document) => document in userPersonalInformation) || 'voterNr';

    const redirectToConfirmationScreen = (confirmationScreenProps: IResultScreenProps) => {
        nextStepHandler({
            ...FORM_STEPS.CONFIRMATION,
            PROPS: confirmationScreenProps,
            DONT_SHOW_ICON: true
        });
    };
    useEffect(() => {
        setUserPersonalInformation((prev: IUserPersonalInformation) => ({
            ...prev,
            gender: {
                ...prev.gender,
                isValid: prev.gender.selectedItem?.key !== FALLBACK_GENDER.key,
                isLabelVisible: prev.gender.selectedItem?.key !== FALLBACK_GENDER.key
            }
        }));
    }, [userPersonalInformation.gender.selectedItem]);
    const handleSubmit = (event: any) => {
        event.preventDefault();
        let documentToReview: keyof ISteps;

        if (whitelistedUser) {
            documentToReview = 'METADATA_OTHERS';
        } else {
            documentToReview = 'STANDARD_REVIEW_DETAILS';
        }
        setIsLoading(true);
        const userInfo: UserProps = {
            firstName: userPersonalInformation.firstName.input,
            lastName: userPersonalInformation.lastName.input,
            placeOfBirth: userPersonalInformation.placeOfBirth.input,
            dateOfBirth: userPersonalInformation.dateOfBirth.input,
            gender: userPersonalInformation.gender.selectedItem?.key?.substring(7, 8)?.toUpperCase() as Gender,
            nationality: t(userPersonalInformation.citizen.selectedItem?.key ?? ''),
            documentNumber: (userPersonalInformation[retrieveDocumentNumber()] as IUserDataItem).input,
            town: `${userAddressInformation.town.selectedItem?.description ?? ''} VILLE`,
            street: userAddressInformation.street.input,
            commune: userAddressInformation.commune.input,
            province: userAddressInformation.province.selectedItem!.description,
            othersType: userPersonalInformation.othersType?.selectedItem?.description,
            occupation: (userAdditionalInformation.occupation?.selectedItem)?.value,
            income: userAdditionalInformation.income?.selectedItem?.description,
            idCardIssuingBy: userAdditionalInformation.idCardIssuingBy.input,
            idCardIssuingDate: userAdditionalInformation.idCardIssuingDate.input,
            idCardIssuingPlace: userAdditionalInformation.idCardIssuingPlace?.selectedItem.description || ''
        };
        userManager.saveUserInformation(userInfo);

        let confirmationScreenProps: IResultScreenProps;
        let nextDocType = (documentType || '').toUpperCase();

        const selectedOthersLabel = userPersonalInformation.othersType?.selectedItem?.description
            || userPersonalInformation.othersType?.selectedItem?.label;
        if (isMetaDataForm && selectedOthersLabel) {
            const match = (identityDocuments || []).find((d: any) => [d?.en, d?.fr].some((txt: string) => txt?.toLowerCase()
                === selectedOthersLabel?.toLowerCase())
            );

            nextDocType = match?.documentType?.toUpperCase();
        }

        setDocumentType(nextDocType);
        registrationManager.createAccount(isMetaDataForm, whitelistedUser, nextDocType, identityDocuments ?? [])
            .then(() => {
                eventTracker({
                    name: 'REGISTRATION',
                    objectValue: {
                        SUCCESS: `TOC: ${SELECTED_DOCUMENT}
                         - ${Utils.getTimeInterval(moment()
        .subtract(moment.unix(analyticsManager.getInitTime()).unix(), 'milliseconds').format('mm:ss'))}`
                    }
                });

                eventTracker({
                    name: 'REGISTRATION',
                    objectValue: { SUCCESS: `Selected document: ${SELECTED_DOCUMENT}` }
                });
                if (isAgentLogged) {
                    if (isMetaDataForm) {
                        const docTypeTemp = userPersonalInformation.othersType?.selectedItem?.description;
                        const DOCUMENT_FACE = 'OTHERS_FRONT' as keyof ISteps;
                        const dynamicProps = {
                            nextDocType,
                            docTypeTemp,
                            captureBackSide,
                            selectedDocumentId,
                            selectedDocumentEn,
                            selectedDocumentFr,
                            identityDocuments
                        };
                        nextStepHandler({
                            ...Constants.FORM_STEPS[DOCUMENT_FACE],
                            PROPS: dynamicProps
                        });
                        return;
                    }

                    nextStepHandler({
                        ...FORM_STEPS.CONFIRMATION_METHOD,
                        PROPS: {
                            REVIEW_DOCUMENT: documentToReview,
                            isMetaDataForm
                        }
                    });
                    return;
                }
                if (isMetaDataForm) {
                    const docTypeTemp = userPersonalInformation.othersType?.selectedItem?.description;
                    const DOCUMENT_FACE = 'OTHERS_FRONT' as keyof ISteps;
                    const dynamicProps = {
                        docTypeTemp,
                        captureBackSide,
                        selectedDocumentId,
                        selectedDocumentEn,
                        selectedDocumentFr,
                        identityDocuments
                    };
                    nextStepHandler({
                        ...Constants.FORM_STEPS[DOCUMENT_FACE],
                        PROPS: dynamicProps
                    });
                    return;
                }
                confirmationScreenProps = {
                    success: true,
                    title: 'confirmation.registrationSubmitted',
                    description: 'confirmation.registrationSubmittedDescription',
                    actionText: 'general.rateExperience',
                    actionFn: () => nextStepHandler(Constants.FORM_STEPS.EVALUATE_EXPERIENCE),
                    showActionButton: true
                };
                redirectToConfirmationScreen(confirmationScreenProps);
            },
            (error: BackendError) => {
                const responseMessage = error?.message
                    || error?.errorDescription
                    || error?.response?.data?.message
                    || 'Something went wrong';
                const backendMessage = responseMessage.split('|')[0].trim();

                const backendCode = error?.errorCode
                || error?.response?.data?.errorCode
                || 'UNKNOWN_ERROR';

                const backendCodeNumber = Number(backendCode);
                if (Constants.ERRORS.RECOVERABLE_ACCOUNT_CREATION_ERRORS.includes(backendCodeNumber)) {
                    // NOTE: Checks if the error is recoverable or not, if the error is recoverable a modal is shown.
                    // NOTE: If the error is not recoverable the user is redirected to another screen with more detailed information regarding the error
                    setError({
                        hasError: true,
                        title: backendCode,
                        description: backendMessage
                    });
                    return;
                }
                confirmationScreenProps = {
                    title: backendMessage,
                    description: ERRORS_DESCRIPTION[Number(backendCode)]?.description
                        || ERRORS_DESCRIPTION[ERROR_CODES.GENERIC_ERROR].description,
                    actionText: 'general.tryAgain',
                    actionFn: () => {
                        if (REQUEST_REVERSE_OTP_RECOVERABLE_ERRORS.includes(backendCodeNumber)) {
                            nextStepHandler({
                                ...FORM_STEPS.AGENT_REVERSE_OTP,
                                PROPS: { REVIEW_DOCUMENT: documentToReview }
                            });
                            return;
                        }
                        nextStepHandler(currentFormStep);
                    },
                    showActionButton: true

                };

                redirectToConfirmationScreen(confirmationScreenProps);
            });
    };

    return (
        <>
            {isLoading && <Loader withBackdrop animation={VodafoneWhiteLoader} description={t('general.justAMoment')} />}

            <GeneralInformationScreen
                onSubmit={handleSubmit}
            >
                <div className="wrapper">
                    <div className="title">
                        <span>
                            {isMetaDataForm
                                ? isAgentLogged
                                    ? t('generalInformation.enterDetailsForAgent')
                                    : t('generalInformation.enterDetails')
                                : isAgentLogged
                                    ? t('generalInformation.reviewCustomerDetails')
                                    : t('generalInformation.reviewDetails')}
                        </span>
                    </div>
                    <div className="subtitle">
                        <span>
                            {isMetaDataForm
                                ? t('generalInformation.enterDetailsSubTitle') : t('generalInformation.confirmDetails')}
                        </span>
                    </div>

                    <Card cardLabel={t('screenLabels.phoneNumber')} distanceFromLabelToContent={4}>
                        <span className="phone-number">
                            {`${COUNTRY_CODE.code} ${Utils.msisdnFormatter(userManager.getMsisdn())}`}
                        </span>
                    </Card>

                    <Card cardLabel={t('generalInformation.personalInfo')} distanceFromLabelToContent={20}>
                        <ul className="user-data">
                            <FieldRenderer
                                data={userPersonalInformation}
                                setData={setUserPersonalInformation}
                            />
                        </ul>
                    </Card>

                    <Card cardLabel={t('generalInformation.addressInfo')} distanceFromLabelToContent={20}>
                        <ul className="user-data">
                            <FieldRenderer
                                data={userAddressInformation}
                                setData={setUserAddressInformation}
                            />
                        </ul>
                    </Card>

                    <Card cardLabel={t('generalInformation.additionalData')} distanceFromLabelToContent={20}>
                        <ul className="user-data">
                            <FieldRenderer
                                data={userAdditionalInformation}
                                setData={setUserAdditionalInformation}
                            />
                        </ul>
                    </Card>

                    {(!isMetaDataForm) && (
                        <Card cardLabel={t('generalInformation.attachedPhotos')} distanceFromLabelToContent={10}>
                            <div className="attached-photos">
                                <div className="personal-document">
                                    <div
                                        onClick={() => showDialogHandler('DOCUMENT')}
                                        className="personal-document-img personal-document--front"
                                        id="edit-document-button"
                                    >
                                        <img className="attached-photos__img" src={documentImage.FRONT} alt="Document front" />
                                    </div>

                                    {documentImage.BACK && (
                                        <div className="personal-document-img">
                                            <img className="attached-photos__img" src={documentImage.BACK} alt="Document back" />
                                        </div>
                                    )}
                                </div>

                                <div onClick={() => showDialogHandler('SELFIE')} className="selfie" id="edit-selfie-button">
                                    <img className="selfie__img" src={challengeManager.retrieveCapturedSelfie()} alt="Selfie" />
                                </div>
                            </div>
                        </Card>
                    )}

                    <Card cardLabel={t('generalInformation.additionalDocuments')} distanceFromLabelToContent={20}>
                        {
                            additionalDocs.length < MAX_ADDITIONAL_DOCS && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginBottom: additionalDocs.length > 0 ? '16px' : '',
                                    borderBottom: additionalDocs.length > 0 ? '#dadada solid 1px' : ''
                                }}
                                >
                                    <div
                                        onClick={() => {
                                            showDialogHandler('ADDITIONAL', additionalDocs.length + 1);
                                        }}
                                        className="upload-icon"
                                    >
                                        <CloudUploadIcon width={80} />
                                    </div>
                                </div>
                            )
                        }
                        <div className="attached-photos">
                            <div className="personal-document">
                                {
                                    additionalDocs.map((doc, arrayIndex) => (
                                        <>
                                            {
                                                doc && (
                                                    <div
                                                        onClick={() => showDialogHandler('ADDITIONAL', arrayIndex)}
                                                        className="personal-document-img personal-document--front"
                                                        id="edit-selfie-button"
                                                    >
                                                        <img className="attached-photos__img" src={doc} alt="Selfie" />
                                                    </div>
                                                )
                                            }
                                        </>
                                    ))
                                }
                            </div>
                        </div>
                    </Card>

                    {isAgentLogged && (!isMetaDataForm)
                        && (
                            <div className="agent-confirmation">
                                <Checkbox
                                    id="consent-data-usage-checkbox"
                                    checked={agentConfirmation}
                                    onChangeFn={(confirmed: boolean) => setAgentConfirmation(confirmed)}
                                />
                                <div className="confirmation__description">
                                    <span>{t('generalInformation.agentConfirmation')}</span>
                                </div>
                            </div>
                        )}
                </div>
                <div className="footer">
                    <Button
                        type="submit"
                        mode="normal"
                        disabled={!(validAddressInformation && validPersonalInformation && validAdditionalInformation
                            && (isMetaDataForm || (agentConfirmation || !isAgentLogged)))}
                        hasShadow
                        id="submit-registration-button"
                    >
                        {t(
                            isAgentLogged
                                ? (
                                    isMetaDataForm ? 'general.submit' : 'reverseOTP.agent.sendReverseOtp'
                                ) : 'general.submit'
                        )}
                    </Button>
                </div>

            </GeneralInformationScreen>

            <Dialog // INFO: Dialog to confirm image edition
                onClose={closeDialog}
                open={showDialog.show}
                title={showDialog.title}
                description={showDialog.description}
                actions={{
                    cancelText: t('general.cancel'),
                    confirmText:
                        imageToEdit === 'ADDITIONAL'
                            ? (!whitelistedUser
                                ? t('generalInformation.add')
                                : (!isMetaDataForm
                                    ? t('generalInformation.edit')
                                    : t('generalInformation.add')))
                            : t('generalInformation.edit'),
                    cancelAction: closeDialog,
                    confirmAction: editImageHandler
                }}
            />

            <Modal // INFO: Modal for recoverable errors
                onClose={() => setError((prev) => ({ ...prev, hasError: false }))}
                open={!!error.hasError}
                title={t(error.title)}
                description={t(error.description)}
            />
        </>
    );
};

export default memo(GeneralInformation);
